import type { MatchAndClubCountryCode, PlayerFFTranking, UserGender } from "@teamz/common";
import type { UrlKeys } from "nuqs";

import { useQuery } from "@tanstack/react-query";
import { matchAndClubCountryCodes, playerFFTranking, playerGenders } from "@teamz/common";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { FilterBarPlayerList } from "@/components/filter-bar/filter-bar-player-list";
import { ListSkeleton } from "@/components/skeleton/list-skeleton";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { getActiveTab } from "@/lib/utils";
import { routes } from "@/routes";

import { PlayerListItem } from "./player-list-item";
import { PlayerListTeaser } from "./player-list-teaser";

type PlayerType = RouterOutputs["player"]["listPlayers"][number];

const searchParams = {
  search: parseAsString.withDefault(""),
  countryFilter: parseAsStringLiteral(matchAndClubCountryCodes),
  genderFilter: parseAsStringLiteral(playerGenders),
  FFTFilter: parseAsStringLiteral(playerFFTranking),
  activeTab: parseAsStringLiteral(["matchs", "tournaments"]).withDefault("matchs"),
};
const searchParamsKeys: UrlKeys<typeof searchParams> = {
  search: "s",
  countryFilter: "c",
  genderFilter: "g",
  FFTFilter: "fft",
  activeTab: "tab",
};

export function PlayerList() {
  const { user } = useMe();
  const isFreemiumClub = user.role === "club" && !user.plan && !user.isAdmin;
  const [{ search, countryFilter, genderFilter, FFTFilter, activeTab }, setSearchFilters] = useQueryStates(searchParams, { urlKeys: searchParamsKeys });
  const trpc = useTRPC();
  // La liste complète est refusée par l'API à un club freemium : on ne la
  // déclenche pas, le teaser a son propre endpoint.
  const { data, isError, isFetching } = useQuery(trpc.player.listPlayers.queryOptions(undefined, { refetchOnWindowFocus: false, enabled: !isFreemiumClub }));
  const { getCountryName } = useTranslationUtils();
  const { t } = useTranslation(["common", "profile"]);
  const breakpoint = useBreakpoints();

  useEffect(() => {
    setSearchFilters({ activeTab: getActiveTab(user.plan) });
  }, [user, setSearchFilters]);

  if (isFreemiumClub) {
    return <PlayerListTeaser />;
  }

  if (isFetching) {
    return <ListSkeleton />;
  }

  if (isError) {
    throw new Error("Failed to fetch players");
  }

  const filterBySearch = (player: PlayerType, search: string) => {
    const searchTerm = search.toLowerCase();
    return (
      player.fullName?.toLowerCase().includes(searchTerm)
      || getCountryName(player.nationalityCode).toLowerCase().includes(searchTerm)
    );
  };

  const filterByCountry = (player: PlayerType, countryFilter: MatchAndClubCountryCode | null) => {
    if (!countryFilter)
      return true;
    return player.matchAvailabilitiesCountries?.includes(countryFilter);
  };

  const filterByGender = (player: PlayerType, genderFilter: UserGender | null) => {
    if (!genderFilter)
      return true;
    return player.gender === genderFilter;
  };

  const filterByFFT = (player: PlayerType, FFTFilter: PlayerFFTranking | null) => {
    if (!FFTFilter)
      return true;
    return player.FFTRanking === FFTFilter;
  };

  const filteredData = data?.filter(player =>
    filterBySearch(player, search)
    && filterByCountry(player, countryFilter)
    && filterByGender(player, genderFilter)
    && filterByFFT(player, FFTFilter)
    && player.isPublic,
  );

  return (
    <div className="container flex flex-col gap-4 md:p-4">
      <FilterBarPlayerList
        listType="players"
        search={search}
        country={countryFilter ?? ""}
        gender={genderFilter ?? ""}
        FFTRanking={FFTFilter ?? ""}
        activeTab={activeTab}
        setSearch={value => setSearchFilters({ search: value })}
        setCountryFilter={value => setSearchFilters({ countryFilter: value })}
        setGenderFilter={value => setSearchFilters({ genderFilter: value })}
        setFFTFilter={value => setSearchFilters({ FFTFilter: value })}
        setActiveTab={value => setSearchFilters({ activeTab: value })}
      />

      {
        filteredData && (
          <div className="flex flex-col gap-4 px-2 pb-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 md:p-0">
            {(breakpoint === "lg" || breakpoint === "xl") && filteredData.length > 0 && (
              <div
                className="hidden grid-cols-[294px_50px_220px_242px_116px] items-center gap-3 rounded-t-[18px] bg-neutral-50 px-2.5 py-2 text-xs font-medium text-neutral-500 lg:grid"
              >
                <span className="p-2">
                  {`${t("common:mobile-nav.players")} (${filteredData.length})`}
                </span>

                <span className="p-2 text-center">
                  Nat.
                </span>

                <div className="flex items-center justify-between">
                  <span className="w-[72px] px-3 py-2 text-center">
                    FFT
                  </span>
                  <span className="w-[72px] px-3 py-2 text-center">
                    UTR
                  </span>
                  <span className="w-[72px] px-3 py-2 text-center">
                    ATP
                  </span>
                </div>

                <span className="p-2 text-center">
                  {t("profile:availability")}
                </span>

                <span className="p-2 text-center">
                  Actions
                </span>
              </div>
            )}

            {filteredData.length
              ? filteredData.map(player => (
                  <PlayerListItem key={player.id} player={player} fromPath={routes.players} availability={activeTab} />
                ))
              : (
                  <span className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0">
                    {t("common:no-player-found")}
                  </span>
                )}
          </div>
        )
      }

    </div>
  );
}
